import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createMmProgram, type GetMmProgramResponse, getMmProgram, getMmProgramDowntime, getMmProgramHistory, getMmPrograms, getQuoteSources } from '@/services/api/mm-programs';
import { MS_PER_DAY } from '@/utils/date';

export const MM_PROGRAMS_QUERY_KEY = ['mm-programs'] as const;
export const MM_PROGRAMS_LIST_QUERY_KEY = [...MM_PROGRAMS_QUERY_KEY, 'list'] as const;
export const mmProgramQueryKey = (pid: string) => [...MM_PROGRAMS_QUERY_KEY, 'detail', pid] as const;
export const mmProgramHistoryQueryKey = (pid: string) => [...MM_PROGRAMS_QUERY_KEY, 'history', pid] as const;
export const mmProgramDowntimeQueryKey = (pid: string) => [...MM_PROGRAMS_QUERY_KEY, 'downtime', pid] as const;

export const DOWNTIME_PREVIEW_DAYS = 7;

const mmProgramsQueryOptions = () =>
  queryOptions({
    queryKey: MM_PROGRAMS_LIST_QUERY_KEY,
    queryFn: () => getMmPrograms(),
  });

const mmProgramQueryOptions = (pid: string) =>
  queryOptions({
    queryKey: mmProgramQueryKey(pid),
    queryFn: () => getMmProgram(pid),
    // A background refetch must not swap the document out from under an open edit.
    refetchOnWindowFocus: false,
  });

const mmProgramHistoryQueryOptions = (pid: string) =>
  queryOptions({
    queryKey: mmProgramHistoryQueryKey(pid),
    queryFn: () => getMmProgramHistory(pid),
  });

/** The window starts when the query runs, so a refetch moves it forward rather than the key changing on every render. */
const mmProgramDowntimeQueryOptions = (pid: string, instrument: string) =>
  queryOptions({
    queryKey: [...mmProgramDowntimeQueryKey(pid), instrument],
    queryFn: () => {
      const now = Date.now();
      return getMmProgramDowntime(pid, { instrument, from: new Date(now).toISOString(), to: new Date(now + DOWNTIME_PREVIEW_DAYS * MS_PER_DAY).toISOString() });
    },
  });

export const useMmPrograms = () => useQuery(mmProgramsQueryOptions());

export const useMmProgram = (pid: string) => useQuery(mmProgramQueryOptions(pid));

type DocumentResults = { data?: GetMmProgramResponse; isPending: boolean; isError: boolean; error: Error | null }[];

// Module-level so useQueries can keep the combined result stable between renders.
const combineDocuments = (results: DocumentResults) => ({
  documents: results.flatMap((result) => (result.data ? [{ pid: result.data.marketMaker.p, ...result.data }] : [])),
  isPending: results.some((result) => result.isPending),
  errors: results.flatMap((result) => (result.error ? [result.error] : [])),
});

/** Every market maker's full document, for views across all of them; shares the cache with the detail page. */
export const useMmProgramDocuments = (pids: string[]) => useQueries({ queries: pids.map((pid) => mmProgramQueryOptions(pid)), combine: combineDocuments });

export const useMmProgramHistory = (pid: string) => useQuery(mmProgramHistoryQueryOptions(pid));

export const useMmProgramDowntime = (pid: string, instrument: string) => useQuery(mmProgramDowntimeQueryOptions(pid, instrument));

export const useSetUpMarketMaker = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMmProgram,
    onSuccess: (response) => {
      queryClient.setQueryData<GetMmProgramResponse>(mmProgramQueryKey(response.marketMaker.p), { marketMaker: response.marketMaker, version: response.version, canEdit: true });
      void queryClient.invalidateQueries({ queryKey: MM_PROGRAMS_LIST_QUERY_KEY });
    },
  });
};

const QUOTE_SOURCES_STALE_MS = 5 * 60 * 1000;

export const useQuoteSources = () => useQuery({ queryKey: [...MM_PROGRAMS_QUERY_KEY, 'quote-sources'], queryFn: getQuoteSources, staleTime: QUOTE_SOURCES_STALE_MS });
